// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { processCaseBuddyTool68Rows } from '../CaseBuddyTool68Processor.js';
import {
  abbreviateCaseBuddyHash,
  buildTsv,
  firstNonEmptyValue,
  normalizeCell,
  normalizeComparisonKey,
  rowHasMeaningfulData
} from './CaseBuddyShared.js';
import {
  analyzeStartingMainTransactions,
  buildMainRawDataCoverage,
  findLastMeaningfulRowIndex,
  normalizeCaseRowsToWidth,
  rowLooksLikeCaseBuddyHeader,
  trimOuterEmptyRows
} from './CaseBuddySheetData.js';
import { CASE_DEFAULT_COLS, CASE_DEFAULT_ROWS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const TOOL68_COMPAT_HEADER = Object.freeze([
  'Label',
  'Hash',
  'FromAddress',
  'ToAddress',
  'Date',
  'Amount',
  'TokenSymbol',
  'Amount1',
  'Amount2',
  'Notes',
  '',
  '',
  'Funds followed',
  'Balance',
  'From Hash',
  'From Label',
  'Continues as',
  'Left Bounds',
  '',
  '',
  '',
  ''
]);

export function buildRowsSignature(rows = []) {
  const text = buildTsv(Array.isArray(rows) ? rows : []);
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `${text.length}:${(hash >>> 0).toString(16)}`;
}

export function buildRawSetSignature(rawSet = null) {
  if (!rawSet) return '';
  return [
    normalizeComparisonKey(rawSet.rootHash),
    normalizeComparisonKey(rawSet.rootToAddress),
    rawSet.rootRowIndex,
    rawSet.endRowIndex,
    rawSet.rowCount,
    buildRowsSignature(rawSet.rows || [])
  ].join('|');
}

export function buildMainPreparationKey(row = [], fallbackIndex = 0) {
  const pathLabel = normalizeComparisonKey(row?.[0]);
  const hash = normalizeComparisonKey(row?.[1]);
  const toAddress = normalizeComparisonKey(row?.[3]);
  return [pathLabel || `row-${fallbackIndex}`, hash || 'blank-hash', toAddress || 'any-to'].join('|');
}

export function isClearlyBitcoinCaseRow(row = []) {
  const chainId = normalizeCell(row?.[19]).toLowerCase();
  const chainName = normalizeCell(row?.[20]).toLowerCase();
  const token = normalizeCell(row?.[6]).toUpperCase();
  return token === 'BTC' || chainId === '0' || chainId === 'btc' || chainName.includes('bitcoin') || chainName === 'btc';
}

export function rawRowsClearlyBitcoin(rows = []) {
  return (Array.isArray(rows) ? rows : [])
    .filter(Array.isArray)
    .some(row => isClearlyBitcoinCaseRow(row));
}

export function normalizeTool68ChainMode(requestedChain = 'AUTO', leadRowOrRows = []) {
  const chain = normalizeCell(requestedChain).toUpperCase();
  if (chain === 'BTC' || chain === 'BITCOIN') return 'BTC';
  if (chain === 'ETH' || chain === 'EVM' || chain === 'ETHEREUM') return 'ETH';

  if (Array.isArray(leadRowOrRows) && leadRowOrRows.some(Array.isArray)) {
    return rawRowsClearlyBitcoin(leadRowOrRows) ? 'BTC' : 'ETH';
  }

  return isClearlyBitcoinCaseRow(leadRowOrRows) ? 'BTC' : 'ETH';
}

export function runTool68BFlowAnalysis(inputRows = [], options = {}) {
  const chain = options.chain === 'BTC' ? 'BTC' : 'ETH';
  const baseLabel = normalizeCell(options.baseLabel) || '1';
  const labelLongestPathFirst = options.labelLongestPathFirst !== false;
  const pruneBTCUnfollowed = options.pruneBTCUnfollowed !== false;
  const extraSpacing = !!options.extraSpacing;

  const result = processCaseBuddyTool68Rows(inputRows, {
    chain,
    Chain: chain,
    baseLabel,
    BaseLabel: baseLabel,
    labelLongestPath: labelLongestPathFirst,
    LabelLongestPathFirst: labelLongestPathFirst,
    pruneBTCUnfollowed,
    PruneBTCUnfollowed: pruneBTCUnfollowed,
    extraSpacing,
    ExtraSpacing: extraSpacing
  });

  const missingLabels = Array.isArray(result?.missingLabels) ? result.missingLabels : [];
  const userFeedback = normalizeCell(result?.userFeedback);
  const errorText = normalizeCell(result?.error);
  const feedback = result?.success
    ? `${userFeedback ? `${userFeedback}\n` : ''}✅ Workflow complete.`
    : `${userFeedback ? `${userFeedback}\n` : ''}${errorText || '❌ Tool 68 did not complete successfully.'}`;

  return {
    success: !!result?.success,
    outputRows: normalizeCaseRowsToWidth(result?.rows || []),
    duplicateCount: Number(result?.duplicateCount) || 0,
    notesUpdated: Number(result?.notesUpdated) || 0,
    tokenMismatchCount: Number(result?.tokenMismatchCount) || 0,
    missingLabelEntries: missingLabels,
    changesLog: Array.isArray(result?.changesLog) ? result.changesLog.slice() : [],
    feedback
  };
}

function collapseTool68InputSpacerRows(rows = []) {
  const collapsed = [];
  let previousBlank = false;

  rows.forEach((row) => {
    const blank = !rowHasMeaningfulData(row);
    if (blank && previousBlank) return;
    collapsed.push(row);
    previousBlank = blank;
  });

  while (collapsed.length > 0 && !rowHasMeaningfulData(collapsed[0])) {
    collapsed.shift();
  }

  while (collapsed.length > 0 && !rowHasMeaningfulData(collapsed[collapsed.length - 1])) {
    collapsed.pop();
  }

  return collapsed;
}

export function buildTool68InputRowsForRawSet(rawSet = null) {
  if (!rawSet || !Array.isArray(rawSet.rows)) {
    return [];
  }

  const rawRows = collapseTool68InputSpacerRows(
    normalizeCaseRowsToWidth(rawSet.rows)
      .filter(row => !rowLooksLikeCaseBuddyHeader(row))
  );

  if (!rawRows.some(rowHasMeaningfulData)) {
    return [];
  }

  return [
    TOOL68_COMPAT_HEADER.slice(),
    ...rawRows
  ];
}

export function buildMainTransactionBlocks(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const analysis = analyzeStartingMainTransactions(data);
  const mainRows = analysis.mainTransactionRows.slice().sort((left, right) => left.rowIndex - right.rowIndex);
  const lastMeaningfulRowIndex = findLastMeaningfulRowIndex(data);

  const blocks = mainRows.map((mainRow, index) => {
    const startRowIndex = mainRow.rowIndex;
    const nextStartRowIndex = mainRows[index + 1]?.rowIndex ?? -1;
    const replaceEndRowIndex = nextStartRowIndex >= 0
      ? Math.max(startRowIndex, nextStartRowIndex - 1)
      : Math.max(startRowIndex, lastMeaningfulRowIndex);
    let contentEndRowIndex = replaceEndRowIndex;

    while (contentEndRowIndex > startRowIndex && !rowHasMeaningfulData(data[contentEndRowIndex])) {
      contentEndRowIndex -= 1;
    }

    const rows = data.slice(startRowIndex, contentEndRowIndex + 1);
    return {
      id: `main-block-${startRowIndex}-${index}`,
      index,
      startRowIndex,
      contentEndRowIndex,
      replaceEndRowIndex,
      nextStartRowIndex,
      spreadsheetRowNumber: startRowIndex + 1,
      leadRow: data[startRowIndex] || [],
      rows,
      replaceRows: data.slice(startRowIndex, replaceEndRowIndex + 1)
    };
  });

  return { analysis, blocks };
}

export function blockHasPreparedSaplingRows(block = {}) {
  const rows = Array.isArray(block.rows) ? block.rows.filter(rowHasMeaningfulData) : [];
  if (rows.length === 0) return false;

  const leadRow = block.leadRow || rows[0] || [];
  const leadHashKey = normalizeComparisonKey(leadRow?.[1]);
  const leadLabel = normalizeCell(leadRow?.[0]);
  const qValues = rows.map(row => normalizeCell(row?.[16]).toLowerCase()).filter(Boolean);
  const hasPathEnd = qValues.some(value => value.includes('path ends'));
  const hasContinuation = qValues.some(value => value.includes('continues as'));
  const hasFromHashRows = rows.some(row => normalizeCell(row?.[14]));
  const hasDescendantHashRows = rows.slice(1).some((row) => {
    const hashKey = normalizeComparisonKey(row?.[1]);
    if (!hashKey) return false;
    if (normalizeCell(row?.[14])) return true;
    if (leadHashKey && hashKey !== leadHashKey) return true;
    const label = normalizeCell(row?.[0]);
    return !!label && label !== leadLabel;
  });
  const hasDerivedLabels = rows.slice(1).some(row => normalizeCell(row?.[0]));

  return hasPathEnd || hasContinuation || hasFromHashRows || hasDescendantHashRows || hasDerivedLabels;
}

export function buildSaplingPreparationAnalysis(mainData = [], rawData = [], workflowState = {}) {
  const coverage = buildMainRawDataCoverage(mainData, rawData);
  const { analysis, blocks } = buildMainTransactionBlocks(mainData);
  const coverageByRowIndex = new Map((coverage.coverageRows || []).map(row => [row.rowIndex, row]));
  const preparationRecords = workflowState?.tool68PreparedByMainKey && typeof workflowState.tool68PreparedByMainKey === 'object'
    ? workflowState.tool68PreparedByMainKey
    : {};

  const rows = blocks.map((block) => {
    const leadRow = block.leadRow || [];
    const key = buildMainPreparationKey(leadRow, block.startRowIndex);
    const coverageRow = coverageByRowIndex.get(block.startRowIndex) || null;
    const matchedSet = coverageRow?.matchedSet || null;
    const rawSetSignature = buildRawSetSignature(matchedSet);
    const record = preparationRecords[key] || null;
    const processed = blockHasPreparedSaplingRows(block);
    const hasMatchingAppliedRawSet = !!(processed && record?.rawSetSignature && rawSetSignature && record.rawSetSignature === rawSetSignature);
    const hasNewerRawSet = !!(processed && record?.rawSetSignature && rawSetSignature && record.rawSetSignature !== rawSetSignature);
    const rawCovered = coverageRow?.status === 'covered' && !!matchedSet;
    let status = 'unprocessed';
    let statusLabel = 'Needs processing';
    let message = 'Matching raw data is available and this main transaction has not been prepared into a sapling block yet.';

    if (!rawCovered) {
      status = coverageRow?.status === 'to-mismatch' ? 'to-mismatch' : 'raw-missing';
      statusLabel = coverageRow?.statusLabel || 'Raw data missing';
      message = coverageRow?.message || 'Load matching raw data before preparing this main transaction.';
    } else if (hasNewerRawSet) {
      status = 'reprocess-available';
      statusLabel = 'Newer raw set available';
      message = 'This main transaction appears processed, but the current bottom-most raw set differs from the raw set recorded for the last Tool 68 run. Reprocess if the newer raw data should replace the current flow.';
    } else if (processed && hasMatchingAppliedRawSet) {
      status = 'processed-current';
      statusLabel = 'Processed';
      message = 'This main transaction was prepared with the current matching raw set.';
    } else if (processed) {
      status = 'processed-detected';
      statusLabel = 'Processed detected';
      message = 'Main Data already contains prepared sapling markers such as Path ends, Continues as, From Hash values, descendant rows, or derived labels.';
    }

    return {
      id: `sapling-row-${block.startRowIndex}-${normalizeComparisonKey(leadRow?.[1]) || 'blank'}`,
      key,
      rowIndex: block.startRowIndex,
      spreadsheetRowNumber: block.spreadsheetRowNumber,
      blockStartRowIndex: block.startRowIndex,
      blockContentEndRowIndex: block.contentEndRowIndex,
      blockReplaceEndRowIndex: block.replaceEndRowIndex,
      blockRowCount: block.rows.filter(rowHasMeaningfulData).length,
      pathLabel: normalizeCell(leadRow?.[0]),
      hash: normalizeCell(leadRow?.[1]),
      shortHash: abbreviateCaseBuddyHash(leadRow?.[1]),
      toAddress: normalizeCell(leadRow?.[3]),
      shortToAddress: abbreviateCaseBuddyHash(leadRow?.[3]),
      utcDate: normalizeCell(leadRow?.[4]),
      chainId: normalizeCell(leadRow?.[19]),
      chainName: normalizeCell(leadRow?.[20]),
      leadRow,
      block,
      coverageRow,
      rawCovered,
      rawSet: matchedSet,
      rawSetSignature,
      rawRootRowNumber: matchedSet ? matchedSet.displayRootRow : '',
      rawRowCount: matchedSet ? matchedSet.rowCount : 0,
      processed,
      hasMatchingAppliedRawSet,
      hasNewerRawSet,
      record,
      status,
      statusLabel,
      message,
      canProcess: rawCovered,
      include: rawCovered && !processed,
      requiresReprocessConfirmation: processed
    };
  });

  const total = rows.length;
  const rawMissing = rows.filter(row => !row.rawCovered).length;
  const processedCount = rows.filter(row => row.processed && !row.hasNewerRawSet).length;
  const unprocessedCount = rows.filter(row => row.rawCovered && !row.processed).length;
  const reprocessAvailableCount = rows.filter(row => row.hasNewerRawSet).length;
  const ready = total > 0 && analysis.trashRowCount === 0 && rawMissing === 0 && unprocessedCount === 0 && reprocessAvailableCount === 0;

  return {
    analysis,
    coverage,
    rows,
    total,
    rawMissing,
    processedCount,
    unprocessedCount,
    reprocessAvailableCount,
    ready
  };
}

export function normalizeTool68ProcessedRowsForMainRewrite(existingBlock = {}, processedRows = [], options = {}) {
  const sourceRows = trimOuterEmptyRows(normalizeCaseRowsToWidth(processedRows));
  const existingRows = Array.isArray(existingBlock.rows) ? existingBlock.rows : [];
  const existingLead = existingBlock.leadRow || existingRows[0] || [];
  const safeLeadHash = normalizeCell(existingLead?.[1]);
  const fallbackChainId = normalizeCell(existingLead?.[19]);
  const fallbackChainName = normalizeCell(existingLead?.[20]);
  const fallbackHopLogic = normalizeCell(existingLead?.[21]);
  const fallbackBaseLabel = firstNonEmptyValue(options.baseLabel, existingLead?.[0], '1');

  const normalized = sourceRows.map((source, index) => {
    const existing = existingRows[index] || [];
    if (!rowHasMeaningfulData(source)) {
      return Array(CASE_DEFAULT_COLS).fill('');
    }

    const row = Array(CASE_DEFAULT_COLS).fill('');
    row[0] = index === 0 ? firstNonEmptyValue(source?.[0], fallbackBaseLabel) : normalizeCell(source?.[0]);
    row[1] = index === 0 ? firstNonEmptyValue(source?.[1], safeLeadHash) : normalizeCell(source?.[1]);
    row[2] = normalizeCell(source?.[2]);
    row[3] = normalizeCell(source?.[3]);
    row[4] = firstNonEmptyValue(source?.[4], index === 0 ? existingLead?.[4] : existing?.[4]);
    row[5] = normalizeCell(source?.[5]);
    row[6] = normalizeCell(source?.[6]);
    row[7] = '';
    row[8] = '';
    row[9] = normalizeCell(source?.[9]);
    row[10] = firstNonEmptyValue(source?.[10], existing?.[10]);
    row[11] = firstNonEmptyValue(source?.[11], existing?.[11]);
    row[12] = normalizeCell(source?.[12]);
    row[13] = normalizeCell(source?.[13]);
    row[14] = normalizeCell(source?.[14]);
    row[15] = '';
    row[16] = normalizeCell(source?.[16]);
    row[17] = normalizeCell(source?.[17]);
    row[18] = firstNonEmptyValue(source?.[18], existing?.[18]);
    row[19] = firstNonEmptyValue(source?.[19], existing?.[19], fallbackChainId);
    row[20] = firstNonEmptyValue(source?.[20], existing?.[20], fallbackChainName);
    row[21] = firstNonEmptyValue(source?.[21], existing?.[21], fallbackHopLogic);

    return row;
  });

  const trimmed = trimOuterEmptyRows(normalized);
  if (trimmed.length === 0) {
    throw new Error('Tool 68 produced only blank rows.');
  }

  if (!normalizeCell(trimmed[0][1])) {
    trimmed[0][1] = safeLeadHash;
  }

  if (!normalizeCell(trimmed[0][0])) {
    trimmed[0][0] = fallbackBaseLabel;
  }

  if (!normalizeCell(trimmed[0][1])) {
    throw new Error('The prepared entry lost its lead transaction hash.');
  }

  const childHashesByParent = new Map();
  trimmed.forEach((row) => {
    const childHash = normalizeCell(row?.[1]);
    const parentHashKey = normalizeComparisonKey(row?.[14]);
    if (!childHash || !parentHashKey) return;
    const children = childHashesByParent.get(parentHashKey) || [];
    if (!children.some(existing => normalizeComparisonKey(existing) === normalizeComparisonKey(childHash))) {
      children.push(childHash);
    }
    childHashesByParent.set(parentHashKey, children);
  });

  trimmed.forEach((row) => {
    const hash = normalizeCell(row?.[1]);
    if (!hash || !rowHasMeaningfulData(row)) return;
    const existingContinuesAs = normalizeCell(row?.[16]);
    if (existingContinuesAs) return;

    const childHashes = childHashesByParent.get(normalizeComparisonKey(hash)) || [];
    row[16] = childHashes.length > 0
      ? `Continues as ${childHashes.join(', ')}`
      : 'Path ends.';
  });

  return trimmed;
}

export function buildMainDataWithPreparedSaplingReplacements(mainData = [], replacements = [], options = {}) {
  const data = normalizeCaseMatrix(mainData);
  const ordered = (Array.isArray(replacements) ? replacements : [])
    .filter(replacement => Number.isInteger(replacement.blockStartRowIndex) && Number.isInteger(replacement.blockReplaceEndRowIndex))
    .sort((left, right) => right.blockStartRowIndex - left.blockStartRowIndex);
  const addSpacerAfterBlock = options.addSpacerAfterBlock !== false;

  ordered.forEach((replacement) => {
    const preparedRows = trimOuterEmptyRows(normalizeCaseRowsToWidth(replacement.preparedRows || []));
    if (preparedRows.length === 0) return;

    const insertionRows = preparedRows.slice();
    const hasFollowingMainBlock = Number.isInteger(replacement.nextStartRowIndex) && replacement.nextStartRowIndex > replacement.blockStartRowIndex;
    if (addSpacerAfterBlock && hasFollowingMainBlock && rowHasMeaningfulData(insertionRows[insertionRows.length - 1])) {
      insertionRows.push(Array(CASE_DEFAULT_COLS).fill(''));
    }

    const deleteCount = Math.max(1, replacement.blockReplaceEndRowIndex - replacement.blockStartRowIndex + 1);
    data.splice(replacement.blockStartRowIndex, deleteCount, ...insertionRows);
  });

  while (data.length < CASE_DEFAULT_ROWS) {
    data.push(Array(CASE_DEFAULT_COLS).fill(''));
  }

  return normalizeCaseMatrix(data);
}

export function buildTool68PreparationAuditRows(processRows = [], summary = {}) {
  return [
    ['Case Buddy Tool 68 Sapling Preparation Audit', '', '', '', '', '', '', '', '', 'Generated by Prepare Raw Data Into Main Data. Do not append this audit back into Main Data.'],
    ['Selected', summary.selected || 0, 'Applied', summary.applied || 0, 'Failed', summary.failed || 0, 'Reprocessed', summary.reprocessed || 0, 'Output Rows', summary.outputRows || 0],
    ['Main Row', 'Path Label', 'Transaction Hash', 'To Address', 'Status', 'Raw Root Row', 'Rows Written', 'Chain Used', 'Message'],
    ...(Array.isArray(processRows) ? processRows : []).map(row => [
      row.spreadsheetRowNumber || '',
      row.pathLabel || '',
      row.hash || '',
      row.toAddress || '',
      row.applied ? 'Applied' : row.statusLabel || row.status || '',
      row.rawRootRowNumber || '',
      row.outputRowCount || '',
      row.chainUsed || '',
      row.resultFeedback || row.message || ''
    ])
  ];
}
